import { z } from 'zod';

/*
 * Time zones are IANA names ("Asia/Kolkata"), detected in the browser with
 * Intl. Timestamps are stored as instants (timestamptz) and shown in the
 * viewer's zone, except a certificate's issue date, which is fixed in the
 * learner's zone when it's issued so every copy shows the same date.
 */

export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

export const timeZoneSchema = z
  .string()
  .min(1)
  .max(64)
  .refine(isValidTimeZone, 'Unknown time zone');

/** "October 12, 2026": a certificate's issue date in the zone it was issued in. */
export function formatCertificateDate(issuedAt: Date | string, timeZone: string): string {
  return new Date(issuedAt).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone,
  });
}

/** Year and month (1–12) of an instant in a zone, e.g. for LinkedIn's issue date. */
export function yearMonthIn(at: Date | string, timeZone: string): { year: number; month: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'numeric',
    timeZone,
  }).formatToParts(new Date(at));
  const get = (t: string) => Number(parts.find((p) => p.type === t)!.value);
  return { year: get('year'), month: get('month') };
}
